import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Copy, RotateCw, Trash2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";

const PRIORITY_ACCENT = "#0EA5E9";

function Row({ p, entry, wallets, serving, counted, onSave, onClear }) {
  const [nodeId, setNodeId] = useState(entry?.node_id || "");
  const [after, setAfter] = useState(entry?.rotate_after ?? 1);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setNodeId(entry?.node_id || ""); setAfter(entry?.rotate_after ?? 1); }, [entry]);

  const dirty = nodeId !== (entry?.node_id || "") || Number(after) !== (entry?.rotate_after ?? 1);
  const save = async () => { setBusy(true); await onSave(p, nodeId, Number(after) || 1); setBusy(false); };
  const clear = async () => { setBusy(true); await onClear(p); setBusy(false); };
  const mine = wallets.filter((w) => w.mine);
  const team = wallets.filter((w) => !w.mine);

  return (
    <tr className="border-t" style={{ borderColor: "rgba(15,18,38,0.06)" }} data-testid={`rotation-row-${p}`}>
      <td className="py-2.5 pr-3">
        <span className="inline-flex items-center gap-1.5">
          <span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{ background: "rgba(14,165,233,0.12)", color: PRIORITY_ACCENT }}>P{p}</span>
          {serving && (
            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
              style={{ background: "rgba(0,134,58,0.12)", color: "#00863A" }}
              data-testid={`rotation-serving-${p}`}>serving now</span>
          )}
        </span>
      </td>
      <td className="py-2.5 pr-3">
        <select value={nodeId} onChange={(e) => setNodeId(e.target.value)} data-testid={`rotation-wallet-${p}`}
          className="w-full max-w-[230px] px-2 py-1.5 rounded-lg text-xs bg-white outline-none"
          style={{ border: "1px solid rgba(15,18,38,0.14)" }}>
          <option value="">— no link —</option>
          {mine.length > 0 && (
            <optgroup label="My wallets">
              {mine.map((w) => <option key={w.id} value={w.id}>{w.name} · {w.code}{w.exact ? "" : " (shared link)"}</option>)}
            </optgroup>
          )}
          {team.length > 0 && (
            <optgroup label="Team wallets">
              {team.map((w) => <option key={w.id} value={w.id}>{w.name} · {w.code}{w.exact ? "" : " (shared link)"}</option>)}
            </optgroup>
          )}
        </select>
        {entry && entry.resolves_to && !entry.exact && (
          <div className="text-[10.5px] text-amber-700 mt-1" data-testid={`rotation-shared-${p}`}>
            This link isn&apos;t unique to {entry.wallet_name} — leads land on <b>{entry.resolves_to}</b>
          </div>
        )}
        {dirty && nodeId && (
          <div className="text-[10.5px] text-amber-700 mt-1 font-semibold" data-testid={`rotation-unsaved-${p}`}>
            Unsaved change — click Save →
          </div>
        )}
      </td>
      <td className="py-2.5 pr-3">
        <input type="number" min="1" value={after} onChange={(e) => setAfter(e.target.value)}
          data-testid={`rotation-after-${p}`}
          className="w-16 px-2 py-1.5 rounded-lg text-xs text-right bg-white outline-none num"
          style={{ border: "1px solid rgba(15,18,38,0.14)" }} />
      </td>
      <td className="py-2.5 pr-3 text-[11px] font-mono text-ink-3" data-testid={`rotation-counts-${p}`}>
        {entry ? <>{entry.approved_count || 0}{serving ? ` (${counted}/${entry.rotate_after})` : ""} · {entry.assigned_count || 0} signed up</> : "—"}
      </td>
      <td className="py-2.5 text-right whitespace-nowrap">
        {dirty && nodeId && (
          <Button size="sm" onClick={save} disabled={busy} data-testid={`rotation-save-${p}`}
            style={{ background: "#0EA5E9", color: "#fff" }}>
            {busy ? <Loader2 size={12} className="animate-spin mr-1" /> : <Check size={12} className="mr-1" />} Save
          </Button>
        )}
        {entry && (
          <Button size="sm" variant="outline" onClick={clear} disabled={busy} className="ml-1"
            title="Clear this priority" data-testid={`rotation-clear-${p}`}>
            <Trash2 size={12} />
          </Button>
        )}
      </td>
    </tr>
  );
}

export default function LinkRotationTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/admin/link-rotation")
    .then((r) => setData(r.data))
    .catch((e) => toast.error(e.response?.data?.detail || "Could not load the rotation"))
    .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const save = async (p, nodeId, after) => {
    if (!nodeId) return;
    try {
      await api.put(`/admin/link-rotation/${p}`, { node_id: nodeId, rotate_after: after });
      toast.success(`P${p} saved`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const clear = async (p) => {
    if (!window.confirm(`Clear P${p}? Registrations will move to the next priority that has a link.`)) return;
    try { await api.delete(`/admin/link-rotation/${p}`); toast.success(`P${p} cleared`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not clear it"); }
  };

  const resetCounts = async () => {
    try { await api.post("/admin/link-rotation/reset-counts"); toast.success("Counts reset — back to P1"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
  };

  if (loading) return <div className="text-center py-10"><Loader2 className="animate-spin mx-auto" /></div>;
  if (!data) return null;

  const byPriority = Object.fromEntries((data.entries || []).map((e) => [e.priority, e]));

  return (
    <div className="space-y-4" data-testid="share-content-rotation">
      <div>
        <h3 className="font-display text-lg font-semibold tracking-tight">Link rotation</h3>
        <p className="text-xs text-ink-3 mt-1 leading-relaxed">
          Everyone who registers from the <b>login page</b> is credited to the link being served here and sent
          straight into that wallet&apos;s funnel. Once a link reaches its number of <b>new members</b>, the next
          priority takes over — after P5 it starts again at P1.
        </p>
      </div>

      <div className="flex items-center justify-between gap-3 rounded-xl px-4 py-3"
        style={{ background: "rgba(14,165,233,0.07)", border: "1px solid rgba(14,165,233,0.22)" }}>
        <div className="min-w-0">
          <div className="kpi-label">Serving right now</div>
          {data.next_url ? (
            <>
              <div className="text-sm font-semibold" data-testid="rotation-next-wallet">
                P{data.current_priority} · {byPriority[data.current_priority]?.wallet_name}
              </div>
              <div className="font-mono text-[11px] text-ink-3 break-all mt-0.5" data-testid="rotation-next-url">{data.next_url}</div>
            </>
          ) : (
            <div className="text-sm text-ink-2" data-testid="rotation-empty">
              Nothing set yet — pick a wallet for P1 and new registrations will start going there.
            </div>
          )}
        </div>
        {data.next_url && (
          <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(data.next_url); toast.success("Copied"); }}
            data-testid="rotation-copy-next"><Copy size={13} className="mr-1.5" /> Copy</Button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="rotation-table">
          <thead>
            <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-ink-3">
              <th className="py-2 pr-3">Priority</th>
              <th className="py-2 pr-3">Wallet link</th>
              <th className="py-2 pr-3" title="Move to the next priority after this many new members">Rotate after</th>
              <th className="py-2 pr-3">New members · sign-ups</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {(data.priorities || []).map((p) => (
              <Row key={p} p={p} entry={byPriority[p]} wallets={data.wallets || []}
                serving={data.current_priority === p} counted={data.counted}
                onSave={save} onClear={clear} />
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between pt-1">
        <p className="text-[11px] text-ink-3">
          A wallet needs its CaryPact invite link on the Wallets screen before it can be picked.
        </p>
        <Button variant="outline" size="sm" onClick={resetCounts} data-testid="rotation-reset-counts">
          <RotateCw size={13} className="mr-1.5" /> Reset counts
        </Button>
      </div>
    </div>
  );
}
